import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MdArrowRightAlt } from "react-icons/md";
import { CreditCard, Info } from 'lucide-react';
import BookTicketList from '../payments/BookTicketList';
import {
    CardElement,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
    AddressElement,
} from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { showError, showSuccess } from '../../stores/slices/commonSlice';
import { failPurchaseAttempt, resetFailPurchaseAttempt } from '../../stores/slices/ticketSlice';
import { setSelectedTicket } from '../../stores/slices/eventSlice';
import { baseUrl } from "../../constant/constant";
import { fraudFields, assessCheckout } from "../../fraud";
import { CARD_FIELDS, CARDHOLDER_NAME_MAX, detectCardBrand, validateCard, validateCardField } from "../../utils/cardValidation";
import OtpChallenge from "./OtpChallenge";
import { useNavigate } from 'react-router-dom';

/* Server codes that carry a definite verdict about the charge. They ride on a
 * 5xx (PAYMENT_INDETERMINATE is a 502), so the "lost response" recovery must
 * skip them: each has its own handling below that deliberately does NOT retry
 * or poll. */
const PAYMENT_OUTCOME_CODES = ['PAYMENT_INDETERMINATE', 'DUPLICATE_TRANSACTION'];

const stripeCardElementStyle = {
    base: {
        fontSize: '15px',
        color: '#111827',
        fontFamily: 'inherit',
        '::placeholder': {
            color: '#9CA3AF',
        },
    },
    invalid: {
        color: '#E53935',
    },
};

/* ---------------------------------------------------------------------------
 * 3DS challenge window — mobile rendering
 *
 * PowerTranz returns `redirectData`: a self-posting HTML document that hands the
 * buyer to the issuer's ACS page. We write it into a blank window. That document
 * is authored for desktop and carries no viewport meta, so a phone lays it out
 * at the ~980px fallback width and zooms out — the challenge (and often the OTP
 * field) comes up tiny and needs pinch-zooming to use.
 *
 * We can't restyle the issuer's page, but we can give the document we write a
 * correct viewport and stop its content overflowing the screen. Both are
 * inherited by the ACS page only insofar as it sets its own; issuer pages are
 * generally responsive once the viewport is right.
 * ------------------------------------------------------------------------- */
// How long the buyer waits on the bank before we say so and make the
// open-in-a-new-tab fallback prominent. Shared by the loader copy inside the
// 3DS document and the parent overlay so both change together.
const THREE_DS_SLOW_AFTER_MS = 20000;

// The in-app 3DS frame goes through several documents before the challenge is
// on screen: our self-posting form, the gateway's own page(s) — which are
// blank and forward themselves on — and only then the bank's. Each fires
// `load`, and they are cross-origin, so the parent cannot tell which one is the
// bank's. What it CAN see is that the forwarding pages are followed quickly by
// another load and the challenge is not. So the frame counts as loaded once a
// load has gone this long without being followed by another.
const THREE_DS_SETTLE_MS = 4000;
// Upper bound on that wait, measured from the first gateway page. A chain that
// keeps producing loads must not keep a finished challenge covered.
const THREE_DS_SETTLE_MAX_MS = 15000;

const isMobileViewport = () => {
    try {
        // Width alone missed a phone held in landscape, which is wider than the
        // breakpoint and still very much a phone — it got the sized popup window
        // and the desktop challenge box. A coarse pointer means a finger.
        return window.matchMedia('(max-width: 767px)').matches
            || window.matchMedia('(pointer: coarse) and (max-width: 1024px)').matches;
    } catch (_) {
        return false;
    }
};

const MOBILE_VIEWPORT_HEAD =
    '<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">'
    + '<style>'
    + 'html,body{margin:0;padding:0;width:100%;max-width:100%;overflow-x:hidden;'
    + '-webkit-text-size-adjust:100%;text-size-adjust:100%;'
    + "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif}"
    + 'img,table,iframe,form,input,select,textarea{max-width:100%}'
    + 'iframe{width:100%;border:0}'
    + 'input,select,textarea,button{font-size:16px}'  /* <16px triggers iOS auto-zoom */
    + '</style>';

/**
 * Inject the viewport/reset into the 3DS document. Handles the three shapes the
 * gateway can return: a full document with a <head>, one with only <html>, and a
 * bare form fragment.
 *
 * A viewport the gateway already declares is REPLACED, not respected: it used to
 * be left alone, so a redirect page declaring a desktop width pinned the
 * challenge to that width on a phone — the one case this function exists for.
 */
const withMobileViewport = (html) => {
    let doc = String(html ?? '');
    if (!doc) return doc;
    doc = doc.replace(/<meta[^>]+name\s*=\s*["']?viewport["']?[^>]*>/gi, '');
    if (/<head[^>]*>/i.test(doc)) {
        return doc.replace(/<head([^>]*)>/i, `<head$1>${MOBILE_VIEWPORT_HEAD}`);
    }
    if (/<html[^>]*>/i.test(doc)) {
        return doc.replace(/<html([^>]*)>/i, `<html$1><head>${MOBILE_VIEWPORT_HEAD}</head>`);
    }
    return `<!doctype html><html><head>${MOBILE_VIEWPORT_HEAD}</head><body>${doc}</body></html>`;
};

/**
 * "Connecting to your bank" loader, injected into the 3DS document we write.
 *
 * redirectData is a self-posting form whose only visible content is nothing:
 * the form is hidden and the script submits it on load. From that moment until
 * the issuer's ACS page commits, the buyer looks at a blank panel (or, on a
 * slow link, the browser's broken-page placeholder) with no sign that anything
 * is happening — the screen that prompted this.
 *
 * The browser keeps showing the CURRENT document until the navigation it
 * started commits, so a loader painted into this document stays on screen for
 * exactly the round-trip to the bank and is replaced by the ACS page the moment
 * it renders. No parent-side timing needed and nothing to cover the challenge.
 *
 * After 20s the copy changes to say it is taking longer than usual; the
 * parent surfaces the open-in-a-new-tab escape hatch on the same clock.
 */
const BANK_LOADER_MARKUP =
    '<div id="aps-bank-loader" role="status" aria-live="polite" style="'
    + 'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;'
    + 'align-items:center;justify-content:center;gap:18px;padding:24px;box-sizing:border-box;'
    + "background:#fff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;"
    + 'text-align:center;color:#111">'
    + '<style>@keyframes aps-spin{to{transform:rotate(360deg)}}'
    + '@keyframes aps-pulse{0%,100%{opacity:.35}50%{opacity:1}}</style>'
    + '<div style="width:56px;height:56px;border-radius:50%;border:5px solid #f3f4f6;'
    + 'border-top-color:#ffea00;animation:aps-spin .9s linear infinite"></div>'
    + '<div>'
    + '<p id="aps-bank-loader-title" style="margin:0;font-size:17px;font-weight:700;line-height:1.3">Connecting to your bank&hellip;</p>'
    + '<p id="aps-bank-loader-sub" style="margin:6px 0 0;font-size:14px;color:#6b7280;line-height:1.4">'
    + 'Your bank will ask you to confirm this payment. Please don&rsquo;t close this window.</p>'
    + '</div>'
    + '<div style="display:flex;gap:6px" aria-hidden="true">'
    + '<span style="width:8px;height:8px;border-radius:50%;background:#ffea00;animation:aps-pulse 1.2s ease-in-out infinite"></span>'
    + '<span style="width:8px;height:8px;border-radius:50%;background:#ffea00;animation:aps-pulse 1.2s ease-in-out .2s infinite"></span>'
    + '<span style="width:8px;height:8px;border-radius:50%;background:#ffea00;animation:aps-pulse 1.2s ease-in-out .4s infinite"></span>'
    + '</div>'
    + '</div>'
    + '<script>setTimeout(function(){'
    + 'var t=document.getElementById("aps-bank-loader-title"),s=document.getElementById("aps-bank-loader-sub");'
    + 'if(t){t.textContent="Still waiting for your bank\u2026";}'
    + 'if(s){s.textContent="This is taking longer than usual. Please keep this window open.";}'
    + '},' + String(THREE_DS_SLOW_AFTER_MS) + ');<\/script>';

const withBankLoader = (html) => {
    const doc = String(html ?? '');
    if (!doc || doc.includes('id="aps-bank-loader"')) return doc;
    // First thing in <body>, so it paints before the auto-submit script runs
    // and sits above the (hidden) form regardless of what else is in there.
    if (/<body[^>]*>/i.test(doc)) {
        return doc.replace(/<body([^>]*)>/i, `<body$1>${BANK_LOADER_MARKUP}`);
    }
    if (/<\/html>/i.test(doc)) {
        return doc.replace(/<\/html>/i, `${BANK_LOADER_MARKUP}</html>`);
    }
    return doc + BANK_LOADER_MARKUP;
};

/**
 * The same document, prepared for an IFRAME rather than a separate window.
 *
 * redirectData is a self-posting form. In a window it can target whatever it
 * likes, but inside a frame a `target` of _top/_parent/_blank would throw the
 * challenge back out to a full page or a new tab — exactly the behaviour we
 * are moving away from. Forms are pinned to _self so the challenge navigates
 * the frame and stays where the buyer can see it in context.
 *
 * A <base> tag is not enough on its own: it only supplies a DEFAULT target, so
 * a form carrying an explicit one would still escape. Hence the script.
 */
const forThreeDsFrame = (html) => {
    const prepared = withBankLoader(withMobileViewport(html));
    const pinTargets =
        '<script>(function(){'
        + 'function pin(){var f=document.forms;'
        + 'for(var i=0;i<f.length;i++){f[i].target="_self";}}'
        + 'pin();'
        + 'document.addEventListener("DOMContentLoaded",pin);'
        + '})();<\/script>';

    // After the viewport block, so the challenge is already laid out correctly
    // by the time the form submits.
    if (/<\/head>/i.test(prepared)) {
        return prepared.replace(/<\/head>/i, `${pinTargets}</head>`);
    }
    return prepared + pinTargets;
};

const fieldLabelClass = 'block text-sm font-semibold text-gray-700';
const fieldInputWrapClass = 'mt-2 flex items-center min-h-[52px] rounded-xl border border-gray-200 bg-white px-4 focus-within:border-gray-400';
const fieldInputWrapErrorClass = '!border-red-500 focus-within:!border-red-500';
const fieldInputClass = 'w-full h-full bg-transparent border-0 outline-none text-[15px] text-gray-900 placeholder:text-gray-400 disabled:cursor-not-allowed';

function CardTypeBadges() {
    return (
        <div className="flex items-center justify-end gap-1.5 flex-nowrap ml-auto">
            <span className="inline-flex items-center justify-center h-[22px] min-w-[34px] px-1.5 rounded text-[9px] font-bold tracking-wide leading-none shrink-0 bg-[#1a1f71] text-white">
                VISA
            </span>
            <span className="inline-flex items-center justify-center h-[22px] min-w-[34px] rounded text-[9px] font-bold tracking-wide leading-none shrink-0 bg-black px-[5px]">
                <span className="flex items-center">
                    <span className="w-3 h-3 rounded-full bg-[#eb001b] -mr-1" />
                    <span className="w-3 h-3 rounded-full bg-[#f79e1b]" />
                </span>
            </span>
            <span className="inline-flex items-center justify-center h-[22px] min-w-[34px] px-1.5 rounded text-[9px] font-bold tracking-wide leading-none shrink-0 bg-[#006fcf] text-white">
                AMEX
            </span>
            <span className="inline-flex items-center justify-center h-[22px] min-w-[34px] px-1.5 rounded text-[9px] font-bold tracking-wide leading-none shrink-0 bg-gray-100 text-gray-700 border border-gray-200">
                DISC
            </span>
        </div>
    );
}

// cardDetails key → input id, so submit can focus the first invalid field.
const CARD_FIELD_IDS = {
    number: 'payment-card-number',
    expiry: 'payment-card-expiry',
    cvv: 'payment-card-cvc',
    cardholderName: 'payment-cardholder-name',
};

function CardFieldError({ id, message }) {
    if (!message) return null;
    return (
        <p id={id} role="alert" className="mt-1.5 mb-0 text-xs font-semibold text-red-600">
            {message}
        </p>
    );
}

function PowerTranzCardFields({ disabled, cardDetails, onCardChange, onCardBlur, errors = {} }) {
    const update = (field) => (event) => onCardChange(field, event.target.value);
    const blur = (field) => () => onCardBlur?.(field);
    const wrapClass = (field) => (errors[field] ? `${fieldInputWrapClass} ${fieldInputWrapErrorClass}` : fieldInputWrapClass);
    const errorProps = (field, id) => ({
        'aria-invalid': errors[field] ? true : undefined,
        'aria-describedby': errors[field] ? `${id}-error` : undefined,
    });
    // Amex is the one brand with a 4-digit code; everything else is 3.
    const cvcLength = detectCardBrand(cardDetails.number)?.cvcLength === 4 ? 4 : 3;

    return (
        <div className="w-full">
            <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="m-0 text-base font-bold text-gray-800 leading-tight shrink-0">Card Information</h3>
                <CardTypeBadges />
            </div>

            <div className="flex flex-col gap-4">
                <div>
                    <label htmlFor="payment-card-number" className={fieldLabelClass}>
                        Card Number
                    </label>
                    <div className={wrapClass('number')}>
                        <input
                            id="payment-card-number"
                            className={fieldInputClass}
                            disabled={disabled}
                            inputMode="numeric"
                            autoComplete="cc-number"
                            placeholder="1234 1234 1234 1234"
                            value={cardDetails.number}
                            onChange={update('number')}
                            onBlur={blur('number')}
                            {...errorProps('number', 'payment-card-number')}
                        />
                        <span
                            className="flex items-center justify-center shrink-0 w-5 h-5 ml-3 text-gray-400 pointer-events-none"
                            aria-hidden="true"
                        >
                            <CreditCard size={20} strokeWidth={1.75} />
                        </span>
                    </div>
                    <CardFieldError id="payment-card-number-error" message={errors.number} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="payment-card-expiry" className={fieldLabelClass}>
                            Expiry Date
                        </label>
                        <div className={wrapClass('expiry')}>
                            <input
                                id="payment-card-expiry"
                                className={fieldInputClass}
                                disabled={disabled}
                                inputMode="numeric"
                                autoComplete="cc-exp"
                                placeholder="MM / YY"
                                value={cardDetails.expiry}
                                onChange={update('expiry')}
                                onBlur={blur('expiry')}
                                {...errorProps('expiry', 'payment-card-expiry')}
                            />
                        </div>
                        <CardFieldError id="payment-card-expiry-error" message={errors.expiry} />
                    </div>
                    <div>
                        <label htmlFor="payment-card-cvc" className={fieldLabelClass}>
                            CVC
                        </label>
                        <div className={wrapClass('cvv')}>
                            <input
                                id="payment-card-cvc"
                                className={fieldInputClass}
                                disabled={disabled}
                                inputMode="numeric"
                                autoComplete="cc-csc"
                                placeholder={cvcLength === 4 ? '1234' : '123'}
                                maxLength={4}
                                value={cardDetails.cvv}
                                onChange={update('cvv')}
                                onBlur={blur('cvv')}
                                {...errorProps('cvv', 'payment-card-cvc')}
                            />
                            <span
                                className="flex items-center justify-center shrink-0 w-5 h-5 ml-3 text-gray-400 pointer-events-none"
                                aria-hidden="true"
                            >
                                <Info size={18} strokeWidth={1.75} />
                            </span>
                        </div>
                        <CardFieldError id="payment-card-cvc-error" message={errors.cvv} />
                    </div>
                </div>

                <div>
                    <label htmlFor="payment-cardholder-name" className={fieldLabelClass}>
                        Cardholder Name
                    </label>
                    <div className={wrapClass('cardholderName')}>
                        <input
                            id="payment-cardholder-name"
                            className={fieldInputClass}
                            disabled={disabled}
                            autoComplete="cc-name"
                            placeholder="Name on card"
                            value={cardDetails.cardholderName}
                            maxLength={CARDHOLDER_NAME_MAX}
                            onChange={update('cardholderName')}
                            onBlur={blur('cardholderName')}
                            {...errorProps('cardholderName', 'payment-cardholder-name')}
                        />
                    </div>
                    <CardFieldError id="payment-cardholder-name-error" message={errors.cardholderName} />
                </div>
            </div>
        </div>
    );
}

function PaymentModalCardFields({ disabled }) {
    const cardNumberRef = useRef(null);

    const elementOptions = useMemo(
        () => ({
            style: stripeCardElementStyle,
            disabled,
        }),
        [disabled]
    );

    const cardNumberOptions = useMemo(
        () => ({
            ...elementOptions,
            placeholder: '1234 1234 1234 1234',
            disableLink: true,
        }),
        [elementOptions]
    );

    const cardExpiryOptions = useMemo(
        () => ({
            ...elementOptions,
            placeholder: 'MM / YY',
        }),
        [elementOptions]
    );

    const cardCvcOptions = useMemo(
        () => ({
            ...elementOptions,
            placeholder: '123',
        }),
        [elementOptions]
    );

    const focusCardNumber = useCallback(() => {
        const cardNumberElement = cardNumberRef.current;
        if (!cardNumberElement || disabled) return;
        try {
            cardNumberElement.focus();
        } catch (_) {
            /* Stripe iframe may not be ready yet */
        }
    }, [disabled]);

    const handleCardNumberRowPointerDown = useCallback(
        (e) => {
            if (disabled) return;
            const target = e.target;
            if (target instanceof Element && target.closest('iframe')) return;
            focusCardNumber();
        },
        [disabled, focusCardNumber]
    );

    return (
        <div className="w-full">
            <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="m-0 text-base font-bold text-gray-800 leading-tight shrink-0">Card Information</h3>
                <CardTypeBadges />
            </div>

            <div className="flex flex-col gap-4">
                <div>
                    <label htmlFor="payment-card-number" className={fieldLabelClass}>
                        Card Number
                    </label>
                    <div
                        className={`${fieldInputWrapClass} cursor-text`}
                        onPointerDown={handleCardNumberRowPointerDown}
                    >
                        <div className="flex-1 min-w-0 flex items-center h-full [&_.StripeElement]:w-full">
                            <CardNumberElement
                                id="payment-card-number"
                                onReady={(el) => {
                                    cardNumberRef.current = el;
                                }}
                                options={cardNumberOptions}
                            />
                        </div>
                        <span
                            className="flex items-center justify-center shrink-0 w-5 h-5 ml-3 text-gray-400 pointer-events-none"
                            aria-hidden="true"
                        >
                            <CreditCard size={20} strokeWidth={1.75} />
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="payment-card-expiry" className={fieldLabelClass}>
                            Expiry Date
                        </label>
                        <div className={fieldInputWrapClass}>
                            <div className="flex-1 min-w-0 flex items-center h-full [&_.StripeElement]:w-full">
                                <CardExpiryElement id="payment-card-expiry" options={cardExpiryOptions} />
                            </div>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="payment-card-cvc" className={fieldLabelClass}>
                            CVC
                        </label>
                        <div className={fieldInputWrapClass}>
                            <div className="flex-1 min-w-0 flex items-center h-full [&_.StripeElement]:w-full">
                                <CardCvcElement id="payment-card-cvc" options={cardCvcOptions} />
                            </div>
                            <span
                                className="flex items-center justify-center shrink-0 w-5 h-5 ml-3 text-gray-400 pointer-events-none"
                                aria-hidden="true"
                            >
                                <Info size={18} strokeWidth={1.75} />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function CheckoutForm(props){
    const amountFormatter = (amount) => {
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);

        return formatted;
    };
    
    const dispatch = useDispatch();
    const [clientSecret, setClientSecret] = useState('');
    const [address, setAddress] = useState('');
    const [cardDetails, setCardDetails] = useState({
        number: '',
        expiry: '',
        cvv: '',
        cardholderName: '',
    });
    const [redirectData, setRedirectData] = useState('');
    const [transactionIdentifier, setTransactionIdentifier] = useState('');
    // Origin of the backend page that posts the 3DS result back to us, taken
    // from the auth response. Messages from any other origin are ignored.
    const [merchantResponseOrigin, setMerchantResponseOrigin] = useState('');
    // 3DS popup handling. The popup is opened once /payment/powertranz/auth
    // responds and the challenge document is written into it — the challenge is
    // shown ONLY in this popup, there is no inline-iframe fallback. If the
    // browser blocks the popup the buyer is asked to allow pop-ups and retry.
    // completingRef mirrors isCompletingPayment for the popup-close watcher,
    // which would otherwise read a stale closure.
    const powerTranzPopupRef = useRef(null);
    const popupWatchRef = useRef(null);
    const completingRef = useRef(false);

    // On a phone the challenge is hosted in a full-screen iframe INSIDE the app
    // (see threeDsHtml below) rather than a separate browser tab. Non-empty
    // means the overlay is on screen.
    const [threeDsHtml, setThreeDsHtml] = useState('');
    // The in-app frame fires `load` once for our own srcDoc (the self-posting
    // form) and then once per page on the way to the bank. The loader used to
    // come down on the SECOND load, which is the gateway's blank forwarding
    // page, not the bank's — so the buyer watched the spinner vanish and then
    // stared at an empty panel for the rest of the trip. It now stays until the
    // loads have settled (THREE_DS_SETTLE_MS). Cross-origin, so a bank page
    // that errors out ALSO counts as loaded; the slow-timer below covers that
    // case by surfacing the open-in-a-new-tab fallback regardless.
    const threeDsLoadsRef = useRef(0);
    const threeDsSettleRef = useRef(null);
    const threeDsSettleCapRef = useRef(null);
    const [bankPageLoaded, setBankPageLoaded] = useState(false);
    const [threeDsSlow, setThreeDsSlow] = useState(false);

    const clearThreeDsSettleTimers = () => {
        clearTimeout(threeDsSettleRef.current);
        clearTimeout(threeDsSettleCapRef.current);
        threeDsSettleRef.current = null;
        threeDsSettleCapRef.current = null;
    };

    useEffect(() => {
        threeDsLoadsRef.current = 0;
        clearThreeDsSettleTimers();
        setBankPageLoaded(false);
        setThreeDsSlow(false);
        if (!threeDsHtml) return undefined;
        const timer = setTimeout(() => setThreeDsSlow(true), THREE_DS_SLOW_AFTER_MS);
        return () => {
            clearTimeout(timer);
            clearThreeDsSettleTimers();
        };
    }, [threeDsHtml]);

    const handleThreeDsFrameLoad = () => {
        threeDsLoadsRef.current += 1;
        // Load #1 is our own srcDoc — nothing has left for the bank yet.
        if (threeDsLoadsRef.current < 2) return;

        const reveal = () => {
            clearThreeDsSettleTimers();
            setBankPageLoaded(true);
        };

        // Every further load restarts the wait: a page that is followed by
        // another one was a forwarding hop, not the challenge.
        clearTimeout(threeDsSettleRef.current);
        threeDsSettleRef.current = setTimeout(reveal, THREE_DS_SETTLE_MS);
        if (!threeDsSettleCapRef.current) {
            threeDsSettleCapRef.current = setTimeout(reveal, THREE_DS_SETTLE_MAX_MS);
        }
    };

    // Tears down BOTH presentations. Every existing exit path already calls
    // this (success, failure, cancel, unmount), so routing the overlay through
    // the same function means none of them can leave it stranded on screen.
    const closePowerTranzPopup = () => {
        if (popupWatchRef.current) {
            clearInterval(popupWatchRef.current);
            popupWatchRef.current = null;
        }
        const popup = powerTranzPopupRef.current;
        if (popup && !popup.closed) {
            popup.close();
        }
        powerTranzPopupRef.current = null;
        setThreeDsHtml('');
    };

    useEffect(() => closePowerTranzPopup, []);
    const [isCompletingPayment, setIsCompletingPayment] = useState(false);
    // Fraud "challenge" (OTP) state. fraudVerifiedRef gates the actual charge:
    // once true (or when no challenge is needed) the payment proceeds.
    const [otpOpen, setOtpOpen] = useState(false);
    const fraudVerifiedRef = useRef(false);
    const cancelledRef = useRef(false);

    const eventDetails=useSelector(state=>state.event.eventDetails);
    const user = useSelector(state => state.auth.user);
    const country = useSelector(state => state.event.country);
    const currency = useSelector(state => state.event.getCountryFeeListDetails.find(detail => detail.country === country)?.currency);
    const failed_purchase_attempts = useSelector(state => state.tickets.failed_purchase_attempts);

    const { total, paymentSuccess, blackList, onBlocked, updatePayButton, updateDisabledButton, button, disabled, selectedEvent, orderSummary, externalFooter = false, formId = 'checkout-form', paymentGateway = 'stripe', couponCode = null, cancelled = false } = props;

    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();

    const [errorMessage, setErrorMessage] = useState('');
    // Per-field messages for the PowerTranz card inputs (see utils/cardValidation).
    const [cardErrors, setCardErrors] = useState({});
    const backendUrl = `${baseUrl}/payment/create-token`;
    const usePowerTranz = paymentGateway === 'powertranz';

    // Set by the parent when the reservation timer expires. Everything the
    // buyer could still use to reach a charge has to come down with it —
    // previously the 3DS challenge stayed open in its own OS window (which no
    // in-page overlay can cover), its close-watcher kept ticking, and finishing
    // the challenge there still posted /powertranz/complete and charged the card
    // minutes after the reservation was gone.
    //
    // Deliberately NOT cancelled: the post-charge reconciliation in
    // Payment.processOrderAfterPayment and pollPowerTranzStatus. Once the card
    // is charged, dropping the completion call would strand a paid-for order the
    // buyer never sees confirmed. That race is already owned by the expiry
    // effect's /payment/cancel-and-refund call, which refunds only when no order
    // exists (has_order).
    useEffect(() => {
        // Today the parent unmounts this form on dismiss, so the reset only
        // matters if that ever changes — but a permanently-dead form is a nasty
        // way to find that out.
        if (!cancelled) {
            cancelledRef.current = false;
            return;
        }
        cancelledRef.current = true;
        closePowerTranzPopup();       // 3DS window + its close-watcher interval
        setOtpOpen(false);            // the z-[1000] fraud challenge
        setRedirectData('');
        setTransactionIdentifier(''); // detaches the 3DS postMessage listener
        setIsCompletingPayment(false);
        completingRef.current = false;
        setErrorMessage('');
    }, [cancelled]);

    // Ticket-count signals shared by both gateway flows. The fraud gate's
    // `quantity` is cast to int server-side, so it must be the numeric count;
    // the per-type summary string is only for Stripe's human-readable
    // description.
    const ticketCount = selectedEvent?.selectedTicket?.length || 0;
    const ticketQtySummary = () => {
        const countByName = (selectedEvent?.selectedTicket || []).reduce((acc, item) => {
            acc[item.name] = (acc[item.name] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(countByName)
            .map(([name, count]) => `${name} - ${count} ticket${count > 1 ? 's' : ''}`)
            .join(', ');
    };

    const resetPayButton = () => {
        updatePayButton(`Pay Now ${currency || ''} $${amountFormatter(total)}`);
    };

    // The server flags a security block with `type: 'Blocked'` across every
    // payment gate — Stripe intent, Stripe decline settlement, PowerTranz auth,
    // and PowerTranz complete. Show the security-restriction message and close
    // the checkout modal. Returns true when handled so callers can bail before
    // doing anything else.
    const handleBlockedResponse = (json) => {
        if (json && json.type === 'Blocked') {
            dispatch(showError('This purchase cannot be completed due to security restrictions. Please contact Apsession Support if you believe this decision was made in error.'));
            onBlocked?.();
            return true;
        }
        return false;
    };

    // `strike: false` for outcomes that are not a card decline — a lost
    // response, a server fault, an unresolved poll. The buyer may well have
    // been charged on those, and counting them would push a legitimate buyer
    // toward the 3-strike bounce for our own outage.
    const registerPaymentFailure = (message, { strike = true } = {}) => {
        closePowerTranzPopup();
        setRedirectData('');
        setTransactionIdentifier('');
        setIsCompletingPayment(false);
        completingRef.current = false;
        // Once the reservation has lapsed the buyer is looking at the expiry
        // notice. Late failures from the work we just tore down still need the
        // cleanup above, but their toast is noise at best — and the 3-strike
        // branch below navigates to "/", which would yank the page out from
        // under the notice. They are also not the buyer's fault, so they must
        // not count toward the fraud strike counter.
        if (cancelledRef.current) return;
        if (!strike) {
            dispatch(showError(message));
            updateDisabledButton(false);
            resetPayButton();
            return;
        }
        if(failed_purchase_attempts >= 3){
            dispatch(resetFailPurchaseAttempt());
            dispatch(showError(message));
            navigate("/");
            return;
        }

        dispatch(failPurchaseAttempt());
        dispatch(showError(message));
        updateDisabledButton(false);
        resetPayButton();
    };

    // Validate a field when the buyer leaves it — but only once it holds
    // something. Tabbing through an empty form should not light every field up
    // red; "required" is reported at submit.
    const handleCardBlur = (field) => {
        if (!String(cardDetails[field] ?? '').trim()) return;
        const message = validateCardField(field, cardDetails);
        // The CVC rule depends on the brand, so a changed number re-judges it.
        const cvvMessage = field === 'number' && cardDetails.cvv
            ? validateCardField('cvv', cardDetails)
            : undefined;
        setCardErrors((prev) => ({
            ...prev,
            [field]: message,
            ...(cvvMessage !== undefined ? { cvv: cvvMessage } : {}),
        }));
    };

    const handleCardChange = (field, value) => {
        // Editing a field withdraws its error; it is judged again on blur.
        setCardErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
        setCardDetails((prev) => {
            if (field === 'number') {
                const digits = value.replace(/\D/g, '').slice(0, 19);
                return { ...prev, number: digits.replace(/(.{4})/g, '$1 ').trim() };
            }

            if (field === 'expiry') {
                const digits = value.replace(/\D/g, '').slice(0, 4);
                const formatted = digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
                return { ...prev, expiry: formatted };
            }

            if (field === 'cvv') {
                return { ...prev, cvv: value.replace(/\D/g, '').slice(0, 4) };
            }

            return { ...prev, [field]: value };
        });
    };

    const buildPowerTranzExpiration = () => {
        const digits = cardDetails.expiry.replace(/\D/g, '');
        if (digits.length !== 4) {
            throw new Error('Enter the card expiration as MM / YY.');
        }

        const month = Number(digits.slice(0, 2));
        if (!month || month < 1 || month > 12) {
            throw new Error('Enter a valid card expiration month.');
        }

        return `${digits.slice(2)}${digits.slice(0, 2)}`;
    };

    // Poll whether the order for a PowerTranz transaction got created, used when
    // the /complete response is lost to a network drop after the card may have
    // already been charged. Mirrors the Stripe pollPaymentStatus flow.
    const pollPowerTranzStatus = (txnId, { intervalMs = 2000, timeoutMs = 30000 } = {}) =>
        new Promise((resolve, reject) => {
            const startedAt = Date.now();
            const tick = async () => {
                try {
                    const res = await fetch(`${baseUrl}/payment/powertranz/status/${encodeURIComponent(txnId)}`);
                    const json = await res.json();
                    if (json?.status === 'complete') return resolve(json);
                    if (json?.status === 'failed')   return reject(new Error(json.message || 'Payment failed.'));
                } catch (_) {
                    /* transient — keep polling */
                }
                if (Date.now() - startedAt >= timeoutMs) {
                    return reject(new Error('Still finalizing your order. Check My Tickets or your email shortly.'));
                }
                setTimeout(tick, intervalMs);
            };
            tick();
        });

    const completePowerTranzPayment = async (nextTransactionIdentifier) => {
        // Reservation gone — never turn a completed 3DS challenge into a charge.
        if (cancelledRef.current) return;
        if (isCompletingPayment || completingRef.current) return;
        setIsCompletingPayment(true);
        completingRef.current = true;
        closePowerTranzPopup();
        updatePayButton("Completing payment...");

        try {
            const res = await fetch(`${baseUrl}/payment/powertranz/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    transactionIdentifier: nextTransactionIdentifier,
                    event_id: eventDetails.id,
                    user_id: user.id,
                    tickets: selectedEvent,
                    country,
                    ip_address: '',
                    // Forwarded to paymentSuccess (powerTranzComplete delegates to
                    // it); server re-validates + redeems. Null when none applied.
                    coupon_code: couponCode || null,
                }),
            });

            const jsonResponse = await res.json().catch(() => ({}));
            if (handleBlockedResponse(jsonResponse)) return;
            if (!res.ok || jsonResponse.status === 'failed') {
                const err = new Error(jsonResponse.message || 'Payment could not be completed.');
                // Carry the server code (PAYMENT_INDETERMINATE / DUPLICATE_TRANSACTION)
                // so the catch can steer the buyer away from a harmful retry.
                err.code = jsonResponse.code || null;
                // Carry the HTTP status too: a 5xx means the server never
                // finished answering, which the catch treats like a dropped
                // connection rather than a decline.
                err.httpStatus = res.status;
                throw err;
            }

            dispatch(showSuccess('Payment is successful!'));
            updatePayButton("Processing order...");
            paymentSuccess(jsonResponse);
        } catch (error) {
            // We never saw a real answer from the completion endpoint — the
            // server may well have charged the card AND created the order. Poll
            // before declaring failure so we don't tell a charged buyer it
            // failed. Two shapes of "no answer":
            //
            //   - fetch threw (TypeError) — the connection dropped.
            //   - the response is a 5xx/408. A PHP fatal (e.g. the request
            //     exceeding max_execution_time midway through order creation)
            //     is rendered by the API exception handler as a 500 JSON, so
            //     fetch RESOLVES and this is not a TypeError. Checking only for
            //     TypeError sent those straight to "payment failed" while the
            //     order, its QR and its confirmation email had all been written.
            //
            // Explicit gateway declines (402) and the coded outcomes handled
            // below surface immediately instead — they carry a real verdict.
            const lostResponse =
                error instanceof TypeError ||
                [408, 500, 502, 503, 504].includes(error.httpStatus);
            if (lostResponse && !PAYMENT_OUTCOME_CODES.includes(error.code)) {
                updatePayButton("Finalizing your tickets…");
                try {
                    const polled = await pollPowerTranzStatus(nextTransactionIdentifier);
                    dispatch(showSuccess('Payment is successful!'));
                    updatePayButton("Processing order...");
                    paymentSuccess(polled);
                    return;
                } catch (pollErr) {
                    setIsCompletingPayment(false);
                    // Unresolved, not declined — the buyer may hold a valid
                    // charge, so this must not cost them a fraud strike.
                    registerPaymentFailure(pollErr.message, { strike: false });
                    return;
                }
            }

            setIsCompletingPayment(false);
            completingRef.current = false;
            closePowerTranzPopup();
            setRedirectData('');
            setTransactionIdentifier('');

            // Indeterminate: the charge may have gone through but we lost the
            // confirmation. Do NOT re-enable the button — an immediate retry
            // would repost the (single-use) token and come back as a duplicate.
            // Park the button and let the buyer check their email; this is not a
            // decline, so it must not count toward the fraud strike counter.
            if (error.code === 'PAYMENT_INDETERMINATE') {
                dispatch(showError(error.message));
                updatePayButton('Confirming your payment…');
                updateDisabledButton(true);
                return;
            }

            // Duplicate: the prior submission already went through. Surface the
            // "check your email before retrying" guidance and re-enable the
            // button (they may switch cards), but don't count it as a fraud
            // strike either.
            if (error.code === 'DUPLICATE_TRANSACTION') {
                dispatch(showError(error.message));
                updateDisabledButton(false);
                resetPayButton();
                return;
            }

            registerPaymentFailure(error.message);
        }
    };

    useEffect(() => {
        if (!transactionIdentifier) return undefined;

        const handleMerchantResponse = (event) => {
            if (cancelledRef.current) return;
            if (merchantResponseOrigin && event.origin !== merchantResponseOrigin) return;
            const data = event.data;
            if (!data || data.type !== 'powertranz-merchant-response') return;
            if (data.transactionIdentifier !== transactionIdentifier) return;

            if (!data.shouldComplete) {
                registerPaymentFailure(data.responseMessage || 'Card authentication was not approved.');
                return;
            }

            completePowerTranzPayment(data.transactionIdentifier);
        };

        window.addEventListener('message', handleMerchantResponse);
        return () => window.removeEventListener('message', handleMerchantResponse);
    }, [transactionIdentifier, isCompletingPayment, merchantResponseOrigin]);

    // True when the PowerTranz card fields are good to send; otherwise marks
    // the bad fields, focuses the first one and returns false.
    //
    // A mistyped card is not a payment attempt: it must not reach the gateway,
    // and it must not go through registerPaymentFailure — that counts a strike,
    // and three of them bounce the buyer off the checkout.
    const validatePowerTranzCard = () => {
        const validationErrors = validateCard(cardDetails);
        const firstInvalid = CARD_FIELDS.find((field) => validationErrors[field]);
        if (!firstInvalid) {
            setCardErrors({});
            setErrorMessage('');
            return true;
        }
        setCardErrors(validationErrors);
        setErrorMessage('Please correct the highlighted card details.');
        document.getElementById(CARD_FIELD_IDS[firstInvalid])?.focus();
        return false;
    };

    const handlePowerTranzSubmit = async (event) => {
        event.preventDefault();

        // handleSubmit has already checked this; it is repeated because the OTP
        // path re-enters here later, and the fields are editable in between.
        if (!validatePowerTranzCard()) {
            updateDisabledButton(false);
            resetPayButton();
            return;
        }

        updateDisabledButton(true);
        updatePayButton("Initiating payment...");

        // Clear any stale popup from a previous attempt. The 3DS window is not
        // opened until /payment/powertranz/auth responds — see below.
        closePowerTranzPopup();

        try{
            const cardNumber = cardDetails.number.replace(/\D/g, '');

            // Resolve the buyer email the same way the Stripe path does: a
            // guest (user.id == 0) has no account email, so fall back to the
            // ticket-holder email captured on the event. Sending user?.email
            // for a guest here is empty, so the email never reaches the fraud
            // engine (fraud_payment_attempts / fraud_sessions) and the device
            // profile's distinct_emails undercounts.
            const buyerEmail = (user?.id == 0)
                ? (selectedEvent?.ticketUserDetails?.[0]?.email || user?.email || '')
                : (user?.email || '');

            // Buyer label used in the description, resolved exactly like the
            // Stripe path: a guest (user.id == 0) has no username, so use the
            // ticket-holder email; otherwise the account username.
            const buyerName = (user?.id == 0)
                ? (selectedEvent?.ticketUserDetails?.[0]?.email || '')
                : (user?.username || '');

            // Billing first name + phone for the PowerTranz BillingAddress block.
            // Prefer the ticket-holder details captured on the event (present for
            // guests and account holders alike), falling back to the account.
            // The backend fills FirstName from the cardholder name if this is blank.
            const buyerFirstName = selectedEvent?.ticketUserDetails?.[0]?.firstName || user?.firstName || '';
            const buyerPhone = selectedEvent?.ticketUserDetails?.[0]?.phone || user?.phone || '';

            const res = await fetch(`${baseUrl}/payment/powertranz/auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: Number(total),
                    currency: currency || 'USD',
                    cardholder_name: cardDetails.cardholderName.trim(),
                    user_id: user?.id,
                    event_id: eventDetails?.id,
                    email: buyerEmail,
                    first_name: buyerFirstName,
                    phone: buyerPhone,
                    quantity: ticketCount,
                    // EMV 3DS ChallengeWindowSize. The issuer's ACS lays the
                    // challenge out for the size the MERCHANT asks for, so a
                    // phone asking for the desktop 600x400 box gets a desktop
                    // page shrunk onto a small screen. Nothing on our side can
                    // restyle it — the ACS page is cross-origin — so the size
                    // has to be right in the request. 05 = full screen.
                    challenge_window_size: isMobileViewport() ? 5 : 4,
                    // Same human-readable reference the Stripe path sends; the
                    // backend forwards it to PowerTranz as ExternalIdentifier
                    // (shows in gateway reporting), e.g. "#942 FBI - Pon Di
                    // River payment from Web by Jadabogle (Early Bird - 1 ticket)".
                    description: '#'+eventDetails?.id+' '+eventDetails?.name+' payment from Web by '+buyerName+' ('+ticketQtySummary()+')',
                    ...fraudFields(),
                    card: {
                        number: cardNumber,
                        cvv: cardDetails.cvv,
                        expiration: buildPowerTranzExpiration(),
                    },
                }),
            });

            const jsonResponse = await res.json().catch(() => ({}));
            if (handleBlockedResponse(jsonResponse)) return;
            if (!res.ok || jsonResponse.status === 'failed' || !jsonResponse.redirectData) {
                throw new Error(jsonResponse.message || 'Card authentication could not be started.');
            }

            setErrorMessage('');
            setTransactionIdentifier(jsonResponse.transactionIdentifier);
            try {
                setMerchantResponseOrigin(new URL(jsonResponse.merchantResponseUrl).origin);
            } catch {
                setMerchantResponseOrigin('');
            }

            // Present the challenge now that /payment/powertranz/auth has
            // responded with redirectData.
            //
            // PHONE → a full-screen iframe inside the app. This is the standard
            // EMV 3DS2 presentation: the merchant supplies the frame and tells
            // the ACS how big it is via ChallengeWindowSize, which is precisely
            // why that field exists. Handing the buyer off to a separate browser
            // tab instead meant the ACS page was on its own, at whatever width
            // it chose, with our chrome gone and no way back if they lost the
            // tab. Framing keeps it in context and full-bleed.
            //
            // DESKTOP → unchanged. The 500x650 popup works there and this is a
            // payment path; there is no reason to put it at risk for a problem
            // that only shows up on phones.
            if (isMobileViewport()) {
                setThreeDsHtml(forThreeDsFrame(jsonResponse.redirectData));
                updatePayButton("Complete card authentication");
                return;
            }

            const popup = window.open('', 'powertranz-3ds', 'width=500,height=650');
            if (!popup || popup.closed) {
                registerPaymentFailure('Please allow pop-ups for this site to complete card authentication, then try again.');
                return;
            }

            powerTranzPopupRef.current = popup;
            popup.document.open();
            popup.document.write(withBankLoader(withMobileViewport(jsonResponse.redirectData)));
            popup.document.close();

            // A closed popup with no completion under way means the buyer
            // abandoned the challenge — reset the form so they can retry.
            popupWatchRef.current = setInterval(() => {
                if (popup.closed) {
                    clearInterval(popupWatchRef.current);
                    popupWatchRef.current = null;
                    powerTranzPopupRef.current = null;
                    // On expiry WE close the popup, so "abandoned the challenge"
                    // is the wrong reading of this and must not be reported.
                    if (!completingRef.current && !cancelledRef.current) {
                        registerPaymentFailure('Card authentication was cancelled.');
                    }
                }
            }, 500);
            updatePayButton("Complete card authentication");
        }
        catch(error){
            setErrorMessage(error.message);
            registerPaymentFailure(error.message);
        }
    };

    const handleStripeSubmit = async (event) => {
        updateDisabledButton(true);
        updatePayButton("Initiating payment ....");
        event.preventDefault();

        if (elements == null || stripe == null) {
          return;
        }

        const { error: submitError } = await elements.submit();
        if (submitError?.message) {
            setErrorMessage(submitError.message);
            updateDisabledButton(false);
            resetPayButton();
            return;
        }

        if(clientSecret == ''){
            try{
                let user_email = '';
                let username = '';

                if(user.id == 0){
                    user_email = selectedEvent.ticketUserDetails[0].email;
                    username = selectedEvent.ticketUserDetails[0].email;
                }
                else{
                    user_email = user.email;
                    username = user.username;
                }

                const cardE = externalFooter
                    ? elements.getElement(CardNumberElement)
                    : elements.getElement(CardElement);
                const { paymentMethod, error } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardE
                });

                if (error) {
                    throw new Error(error.message);
                }
                
                const res = await fetch(backendUrl, {
                    method: 'POST',
                    headers: {
                    'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        user_id: user.id,
                        event_id: eventDetails.id,
                        amount: total * 100,
                        email: user_email,
                        ip_address: '',
                        device_id: '',
                        quantity: ticketCount,
                        ...fraudFields(),
                        description: '#'+eventDetails.id+' '+eventDetails.name+' payment from Web by '+username+' ('+ticketQtySummary()+')',
                        descriptor: eventDetails.name.slice(0, 8)+' apsession.com',
                        currency: currency ? currency.toLowerCase() : 'usd',
                        payment_method: paymentMethod
                    }),
                });

                if (!res.ok) {
                    const jsonResponse = await res.json();
                    if (handleBlockedResponse(jsonResponse)) return;
                    if(jsonResponse.message){
                        throw new Error(jsonResponse.message);
                    }
                    else{
                        throw new Error('Something went wrong. Please try again after sometime.');
                    }
                }
                else{
                    const jsonResponse = await res.json();
                    if (handleBlockedResponse(jsonResponse)) return;
                    if(jsonResponse.status == 'failed'){
                        throw new Error(jsonResponse.message);
                    }
                    else{
                        setClientSecret(jsonResponse.clientSecret);
                    }
                }
            }
            catch(error){
                registerPaymentFailure(error.message);
            }
        }
        else{
            paymentConfirmation();
        }
    };

    useEffect(() => {
        if (!usePowerTranz) {
            paymentConfirmation();
        }
    }, [clientSecret]);

    const paymentConfirmation = () => {
        if(clientSecret !== '' && clientSecret !== undefined && stripe && elements){
            const cardElement = externalFooter
                ? elements.getElement(CardNumberElement)
                : elements.getElement(CardElement);

            stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        address: address,
                    },
                }
            })
            .then(async function (result) {
                if (result.error) {
                    const intentId = result.error.payment_intent?.id;

                    // Settle EVERY decline server-side so payment_attempts /
                    // failed_payments / blacklists get written — not just the
                    // stolen/lost/generic cases the legacy flow reported. The
                    // confirm happens here in the browser, so without this call
                    // the server never learns the attempt was declined and the
                    // attempt row stays stuck at "initiated". AWAIT it: a
                    // blacklist-worthy decline (stolen / lost / suspected fraud)
                    // returns type:Blocked, and we close the checkout with the
                    // security message on THIS first decline rather than waiting
                    // for the retry to be caught at create-token.
                    if (intentId) {
                        let declineJson = {};
                        try {
                            const declineRes = await fetch(`${baseUrl}/payment/decline`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    intent_id: intentId,
                                    user_id: user.id,
                                    email: user.email,
                                    event_id: eventDetails.id,
                                }),
                            });
                            declineJson = await declineRes.json().catch(() => ({}));
                        } catch { /* fall through to the normal decline message */ }

                        if (handleBlockedResponse(declineJson)) return;
                    }

                    if(result.error.decline_code == 'generic_decline'){
                        await fetch(`${baseUrl}/payment/failed`, {
                            method: 'POST',
                            headers: {
                            'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                ticketsData: selectedEvent,
                                event_id: eventDetails.id
                            })
                        });
                    }

                    registerPaymentFailure(result.error.message);

                } else {
                    dispatch(showSuccess('Payment is successful!'));
                    updatePayButton("Processing order...");
                    paymentSuccess(result.paymentIntent.id);
                }
            });
        }
    }

    const rawSubmit = usePowerTranz ? handlePowerTranzSubmit : handleStripeSubmit;

    // Fraud gate (Stage 4) in front of the real submit. Asks the server for the
    // pre-payment decision BEFORE any card is charged:
    //   block            -> refuse, show the message
    //   challenge (otp)  -> open the OTP modal; resume on verify
    //   challenge (3ds)  -> proceed (the processors run their native 3DS)
    //   allow / no session -> proceed
    // Fails open: any error in the gate lets the payment proceed.
    const handleSubmit = async (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        // The form can still be submitted by Enter in a card field even with the
        // Pay button disabled, so the reservation is re-checked here too.
        if (cancelledRef.current) return;

        // Card typos are caught before the fraud gate, not after it: there is
        // no point scoring (or OTP-challenging) a payment that cannot be sent.
        if (usePowerTranz && !validatePowerTranzCard()) return;

        if (fraudVerifiedRef.current) {
            return rawSubmit(event || { preventDefault: () => {} });
        }

        updateDisabledButton(true);
        updatePayButton('Verifying…');

        let assessment = null;
        try {
            assessment = await assessCheckout();
        } catch (_) {
            assessment = null;
        }

        // assessCheckout() is a round trip; the reservation can lapse during it.
        if (cancelledRef.current) return;

        const decision = assessment?.decision || 'approve';

        if (decision === 'decline') {
            updateDisabledButton(false);
            resetPayButton();
            const msg = assessment?.message || 'We are unable to process this payment. Please contact support.';
            setErrorMessage(msg);
            registerPaymentFailure(msg);
            return;
        }

        if (decision === 'challenge' && assessment?.challenge_type === 'otp') {
            updateDisabledButton(false);
            resetPayButton();
            setOtpOpen(true);
            return;
        }

        // approve, or a 3DS challenge handled natively by the processor.
        return rawSubmit(event || { preventDefault: () => {} });
    };

    const handleOtpVerified = (verifiedPhone) => {
        fraudVerifiedRef.current = true;
        setOtpOpen(false);

        // Persist a number the buyer corrected inside the challenge so the order
        // records the phone that was actually verified. This goes through the
        // store rather than a local patch because the two gateways post the cart
        // from different places — PowerTranz from this component, Stripe from
        // Payment.processOrderAfterPayment — and both read state.event.selectTickets.
        const phone = String(verifiedPhone || '').trim();
        const details = selectedEvent?.ticketUserDetails;
        if (phone && Array.isArray(details) && details.length
            && String(details[0]?.phone || '').trim() !== phone) {
            dispatch(setSelectedTicket({
                ...selectedEvent,
                ticketUserDetails: details.map((u) => ({ ...u, phone })),
            }));
        }

        // Verifying an OTP that was on screen when the reservation lapsed must
        // not resume the charge.
        if (cancelledRef.current) return;
        // Resume the payment now that the phone is verified.
        rawSubmit({ preventDefault: () => {} });
    };

    const handleOtpCancel = () => {
        setOtpOpen(false);
        updateDisabledButton(false);
        resetPayButton();
    };

    // `orderSummary` is an optional ReactNode rendered between the card
    // type imagery and the Pay Now button. Payment.jsx passes the new
    // Order Summary card here so the visual order matches the design:
    // card fields → order summary → pay now. When not provided we fall
    // back to the legacy <BookTicketList/> so other callers stay working.
    // Buyer backed out of the challenge. Mirrors what the popup-close watcher
    // reports on desktop, so both presentations fail the same way.
    const handleThreeDsCancel = () => {
        closePowerTranzPopup();
        if (!completingRef.current && !cancelledRef.current) {
            registerPaymentFailure('Card authentication was cancelled.');
        }
    };

    // Escape hatch. An ACS that refuses to be framed (X-Frame-Options /
    // frame-ancestors) renders as a blank panel, and the browser gives the
    // PARENT page no usable signal — cross-origin, so there is nothing to read
    // and `load` fires either way. There is no reliable way to detect it and
    // switch automatically, so the buyer gets a visible way out instead of a
    // dead end.
    const handleThreeDsOpenInTab = () => {
        const html = threeDsHtml;
        const popup = window.open('', 'powertranz-3ds');
        if (!popup || popup.closed) {
            setErrorMessage('Please allow pop-ups for this site, then try again.');
            return;
        }
        powerTranzPopupRef.current = popup;
        popup.document.open();
        popup.document.write(html);
        popup.document.close();
        setThreeDsHtml('');

        popupWatchRef.current = setInterval(() => {
            if (popup.closed) {
                clearInterval(popupWatchRef.current);
                popupWatchRef.current = null;
                powerTranzPopupRef.current = null;
                if (!completingRef.current && !cancelledRef.current) {
                    registerPaymentFailure('Card authentication was cancelled.');
                }
            }
        }, 500);
    };

    return (
        <>
            {/* ---- 3DS challenge, hosted in-app (phones only) ----
                Fixed to the viewport with dvh so an iOS URL bar collapsing
                mid-challenge doesn't clip the OTP field off the bottom.
                z-index clears MUI's Dialog (1300) — the checkout itself is a
                Dialog, and the challenge has to sit above it.

                PORTALED TO <body>. Checkout renders inside a MUI Dialog, and
                Dialog animates with a CSS transform. A transformed ancestor
                becomes the containing block for `position: fixed`, so left in
                place this would anchor to the dialog instead of the viewport —
                the exact bug that makes a "full-screen" overlay come out
                letterboxed inside the modal. */}
            {threeDsHtml && createPortal(
                <div
                    className="fixed inset-0 flex flex-col bg-white"
                    style={{ zIndex: 2000, height: '100dvh' }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Card authentication"
                >
                    <style>{'@keyframes aps-3ds-bar{0%{left:-40%}100%{left:100%}}'}</style>
                    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white shrink-0">
                        <div className="min-w-0">
                            <p className="text-[15px] font-bold text-gray-900 leading-tight">
                                Card authentication
                            </p>
                            <p className="flex items-center gap-1.5 text-xs text-gray-500 leading-tight mt-0.5 truncate">
                                {!bankPageLoaded && (
                                    <span
                                        className="w-3 h-3 shrink-0 rounded-full border-2 border-gray-200 border-t-[#ffea00] animate-spin"
                                        aria-hidden
                                    />
                                )}
                                {bankPageLoaded
                                    ? 'Verify with your bank to finish paying'
                                    : 'Connecting to your bank\u2026'}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleThreeDsCancel}
                            aria-label="Cancel card authentication"
                            className="shrink-0 px-3 py-2 text-sm font-bold text-gray-600 hover:text-black"
                        >
                            Cancel
                        </button>
                    </div>

                    {/* Indeterminate yellow bar under the header until the
                        bank's page commits; then a plain divider. */}
                    <div className="relative h-[3px] bg-gray-200 shrink-0 overflow-hidden" aria-hidden>
                        {!bankPageLoaded && (
                            <div
                                className="absolute top-0 h-full w-[40%] bg-[#ffea00]"
                                style={{ animation: 'aps-3ds-bar 1.2s ease-in-out infinite' }}
                            />
                        )}
                    </div>

                    <div className="relative flex-1 min-h-0">
                        <iframe
                            title="Card authentication"
                            srcDoc={threeDsHtml}
                            onLoad={handleThreeDsFrameLoad}
                            className="w-full h-full border-0"
                            // No sandbox: the challenge needs scripts, forms and its
                            // own origin to function, and a sandbox permissive
                            // enough to allow all three provides no security value.
                            allow="payment"
                        />

                        {/* Same loader the 3DS document paints for itself, but
                            owned by THIS page so it survives the frame's
                            navigations. The one inside the document is replaced
                            by the gateway's first (blank) page; this one stays
                            until the frame has settled on the bank's. */}
                        {!bankPageLoaded && (
                            <div
                                className="absolute inset-0 flex flex-col items-center justify-center gap-[18px] p-6 bg-white text-center"
                                role="status"
                                aria-live="polite"
                            >
                                <style>{'@keyframes aps-3ds-pulse{0%,100%{opacity:.35}50%{opacity:1}}'}</style>
                                <div className="w-14 h-14 rounded-full border-[5px] border-gray-100 border-t-[#ffea00] animate-spin" aria-hidden />
                                <div>
                                    <p className="m-0 text-[17px] font-bold leading-snug text-gray-900">
                                        {threeDsSlow ? 'Still waiting for your bank\u2026' : 'Connecting to your bank\u2026'}
                                    </p>
                                    <p className="mt-1.5 mb-0 text-sm leading-snug text-gray-500">
                                        {threeDsSlow
                                            ? 'This is taking longer than usual. Please keep this window open.'
                                            : 'Your bank will ask you to confirm this payment. Please don\u2019t close this window.'}
                                    </p>
                                </div>
                                <div className="flex gap-1.5" aria-hidden>
                                    {[0, 0.2, 0.4].map((delay) => (
                                        <span
                                            key={delay}
                                            className="w-2 h-2 rounded-full bg-[#ffea00]"
                                            style={{ animation: `aps-3ds-pulse 1.2s ease-in-out ${delay}s infinite` }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={`px-4 py-3 border-t shrink-0 ${threeDsSlow ? 'border-[#ffea00] bg-[#fffbd6]' : 'border-gray-200 bg-white'}`}>
                        {threeDsSlow ? (
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-xs text-gray-700 leading-snug min-w-0">
                                    <span className="font-bold">Taking longer than usual.</span>
                                    {' '}Some banks won&rsquo;t load here.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleThreeDsOpenInTab}
                                    className="shrink-0 px-3 py-2 rounded-full bg-[#ffea00] text-xs font-bold text-black"
                                >
                                    Open in a new tab
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={handleThreeDsOpenInTab}
                                className="text-xs font-semibold text-gray-500 underline underline-offset-2"
                            >
                                Page not loading? Open in a new tab
                            </button>
                        )}
                    </div>
                </div>,
                document.body
            )}

            <OtpChallenge
                open={otpOpen}
                // Seed from what the buyer typed at checkout, not just the
                // account record — otherwise the challenge silently proposes a
                // stale number and the two disagree from the start.
                defaultPhone={
                    selectedEvent?.ticketUserDetails?.[0]?.phone
                    || user?.phone
                    || user?.mobile
                    || ''
                }
                onVerified={handleOtpVerified}
                onCancel={handleOtpCancel}
            />
            <form id={formId} onSubmit={handleSubmit}>
                {usePowerTranz ? (
                    <PowerTranzCardFields
                        disabled={disabled || Boolean(redirectData)}
                        cardDetails={cardDetails}
                        onCardChange={handleCardChange}
                        onCardBlur={handleCardBlur}
                        errors={cardErrors}
                    />
                ) : externalFooter ? (
                    <PaymentModalCardFields
                        disabled={disabled}
                    />
                ) : (
                    <>
                        <h5 className="mb-4" style={{fontSize: '1em'}}>Card Information</h5>
                        <CardElement options={{hidePostalCode: true, disableLink: true, disabled: disabled}} />
                        <hr />
                    </>
                )}

                {errorMessage && (
                    <p className="mt-3 mb-0 text-sm font-semibold text-red-600">{errorMessage}</p>
                )}

                {!usePowerTranz && !disabled && (
                    <>
                        <h5 className={externalFooter ? 'mt-4 mb-3 text-base font-bold text-gray-800 text-center' : 'mt-3 mb-3 text-gray-900 text-lg font-medium'} style={externalFooter ? undefined : {textAlign: 'center'}}>
                            Billing Information
                        </h5>
                        <AddressElement options={{ mode: 'billing' }} onChange={(event) => {
                                if (event.complete) {
                                    setAddress(event.value.address);
                                }
                            }}
                        />
                    </>
                )}

                {!externalFooter && (
                    <center style={{margin: '30px 0'}}><img src="/assets/images/c_types.png" alt="payment.jpg" width="80%" /></center>
                )}

                {!externalFooter && (
                    <>
                        {orderSummary !== undefined ? orderSummary : <BookTicketList/>}

                        <button className='payment-btn btn mt-3' disabled={disabled}>{button}
                            <MdArrowRightAlt className='payment-arrow-icon'/>
                        </button>
                    </>
                )}
            </form>
        </>
    );
}